/**
 * Power BI Embed Service — embed config + report-level filter mapping.
 *
 * Mirrors the existing reactdashboard project: two independent BasicFilters
 * for year and month (cross-product semantics — month restrictions apply
 * across all selected years).
 *
 *   Date.year(s)     → Dimdates[CalendarYear]    (Number[])
 *   Date.month(s)    → Dimdates[MonthLongName]   (String[], full month names)
 *   region           → Region[Country]
 *   territory        → Region[Region]
 *   category         → Product[Category]
 *   salesperson      → Salesperson[Salesperson]
 *
 * Empty arrays → no filter on that column.
 */

const API_BASE = process.env.REACT_APP_API_BASE || '';

// ── Embed config ──────────────────────────────────────────────────

export async function getEmbedConfig() {
  const res = await fetch(`${API_BASE}/api/embed-token`);
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data.error || `Embed token request failed (${res.status})`);
  }
  return res.json();
}

// ── Filter helpers ────────────────────────────────────────────────

export function buildBasicFilter(table, column, operator, values) {
  return {
    $schema: 'http://powerbi.com/product/schema#basic',
    target: { table, column },
    operator,
    values,
    filterType: 1,
  };
}

const MONTH_SHORT_TO_LONG = {
  Jan: 'January',  Feb: 'February', Mar: 'March',     Apr: 'April',
  May: 'May',      Jun: 'June',     Jul: 'July',      Aug: 'August',
  Sep: 'September', Oct: 'October', Nov: 'November',  Dec: 'December',
};

// ── Apply filters ─────────────────────────────────────────────────

export async function applyReportFilters(report, filters) {
  if (!report) return;

  const pbiFilters = [];

  // ── Date: year + month as two BasicFilters (cross-product) ──
  // yearMonths shape: { [year]: string[] } where empty array = "full year"
  const yearMonths = filters.date?.yearMonths || {};
  const yearKeys = Object.keys(yearMonths);
  if (yearKeys.length > 0) {
    pbiFilters.push(
      buildBasicFilter('Dimdates', 'CalendarYear', 'In', yearKeys.map(Number))
    );

    // Collect explicit month selections (only for years where the user
    // narrowed below "full year"). If no year has explicit months, send
    // no month filter — all months across the year set are kept.
    const explicitMonths = new Set();
    for (const yearStr of yearKeys) {
      for (const m of yearMonths[yearStr] || []) {
        explicitMonths.add(MONTH_SHORT_TO_LONG[m] || m);
      }
    }
    if (explicitMonths.size > 0) {
      pbiFilters.push(
        buildBasicFilter('Dimdates', 'MonthLongName', 'In', [...explicitMonths])
      );
    }
  }

  // ── Region → Region[Country] ──
  if (filters.region?.length > 0) {
    pbiFilters.push(
      buildBasicFilter('Region', 'Country', 'In', filters.region)
    );
  }

  // ── Territory → Region[Region] (table and column both named Region) ──
  if (filters.territory?.length > 0) {
    pbiFilters.push(
      buildBasicFilter('Region', 'Region', 'In', filters.territory)
    );
  }

  // ── Product Category ──
  if (filters.category?.length > 0) {
    pbiFilters.push(
      buildBasicFilter('Product', 'Category', 'In', filters.category)
    );
  }

  // ── Salesperson ──
  if (filters.salesperson?.length > 0) {
    pbiFilters.push(
      buildBasicFilter('Salesperson', 'Salesperson', 'In', filters.salesperson)
    );
  }

  console.log('[PBI] Applying filters:', JSON.parse(JSON.stringify(pbiFilters)));
  try {
    await report.setFilters(pbiFilters);
    console.log('[PBI] setFilters OK');
  } catch (err) {
    console.error('[PBI] setFilters failed:', err);
    // Try one at a time to identify which target is the offender
    for (const f of pbiFilters) {
      try {
        await report.setFilters([f]);
        console.log('[PBI] OK in isolation:', f.target);
      } catch (e) {
        console.error('[PBI] REJECTED:', f.target, '→', e?.message || e);
      }
    }
  }
}

// ── Page navigation ───────────────────────────────────────────────

export async function navigateToPage(report, pageName) {
  if (!report) return;
  try {
    const pages = await report.getPages();
    const target = pages.find(
      (p) => p.displayName.toLowerCase() === pageName.toLowerCase()
    );
    if (target) await target.setActive();
  } catch (err) {
    console.error('Failed to navigate page:', err);
  }
}
